import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Camera, ChevronDown, ChevronRight, Cog, FileCheck, FileText, Loader2, Package, Search, ShieldCheck, Star, Users, X } from 'lucide-react';
import type {
  BomItem,
  ComplianceCertificationId,
  EquipmentCatalogEntry,
  SCMCategory,
  Supplier,
  SupplyChainAttachmentRef,
  SupplyChainPlan,
} from '../../../types/supplyChain';
import { SCM_CATEGORY_LABELS } from '../../../types/supplyChain';
import {
  normalizeRequirements,
  suppliersMatchingBomItem,
} from '../../../lib/supplierMatching';
import { getComplianceCertificationById } from '../../../lib/supplyChainBaselines';
import { QUOTE_AVAILABLE_BADGE_MARKER } from '../../../services/supplyChain/processRfqResponse';
import { cn } from '../../../lib/utils';
import { BomStatusControl } from './BomStatusControl';
import type { BomItemStatus } from '../../../types/supplyChain';
import { hasDeliveryReceipt } from '../../../services/supplyChain/deliveryReceipt';
import { BomItemEditDialog } from './BomItemEditDialog';
import { BomItemRowActions } from './BomItemRowActions';
import { catalogEntryToBomDraft } from '../../../lib/matchCatalogToBomItem';
import { labelBomSubstitutability } from '../../../lib/bomFieldLabels';
import { resolveEquipmentCatalog } from '../../../lib/resolveEquipmentCatalog';
import { Button } from '../../ui/button';
import { EmptyState } from '../../ui/aurora';
import { isBomSimulationGenerationEnabled } from '../../../lib/runtimeFlags';

/**
 * SCM12 — true when the BoM item carries the "quote available — review"
 * marker prefixed onto its `notes`. Exported for testability so the
 * needs-review filter on the Quotes panel and the panel-level tests
 * share a single source of truth for the predicate.
 */
export function hasQuoteAvailableBadge(item: BomItem): boolean {
  return item.reviewFlag === 'quote-available'
    || (item.notes ?? '').startsWith(QUOTE_AVAILABLE_BADGE_MARKER);
}

/**
 * Returns the visible portion of the notes — the badge marker is
 * stored as a prefix so we strip it before rendering. Keeps the badge
 * out of the long-form notes preview.
 */
export function notesWithoutBadge(item: BomItem): string {
  const notes = item.notes ?? '';
  if (!notes.startsWith(QUOTE_AVAILABLE_BADGE_MARKER)) return notes;
  return notes.slice(QUOTE_AVAILABLE_BADGE_MARKER.length).trimStart();
}

export interface SupplyChainBomPanelProps {
  plan: SupplyChainPlan | undefined;
  suppliers?: Supplier[];
  /** Owning tenant used to resolve private products and commercial patches. */
  companyId?: string | null;
  onImportBom?: () => void;
  /** Layout → BOM live-link: merge site-works quantities (fence, pads,
   *  roads, clearing) derived from the promoted generation layout's
   *  takeoffs. Undefined when no scenario has been promoted yet. */
  onSyncSiteWorksFromLayout?: () => void | Promise<void>;
  /** Open suppliers for this exact BOM item, retaining its certification filter. */
  onJumpToSuppliersWithFilter?: (filter: readonly ComplianceCertificationId[], bomItemId?: string) => void;
  onCreateItem?: (item: Omit<BomItem, 'id' | 'derivedFrom'>) => void | Promise<void>;
  onCreateSupplier?: (supplier: Pick<Supplier, 'name' | 'hq' | 'categories'>) => Promise<string | null>;
  onUpdateItem?: (itemId: string, item: Omit<BomItem, 'id' | 'derivedFrom'>) => void | Promise<void>;
  onDeleteItem?: (item: BomItem) => void | Promise<void>;
  /** SCM15 — invoked when the user picks a new status from the row dropdown.
   *  The parent owns the persist path (BoM write + status-change-log write
   *  + rollback on failure). When undefined the control falls back to a
   *  read-only pill. */
  onStatusTransition?: (
    item: BomItem,
    toStatus: BomItemStatus,
    opts: { revert: boolean; reason?: string },
  ) => void;
  /** SCM19 — invoked when the site team clicks "Attach delivery photo"
   *  on an in-transit / ordered row. The parent owns the upload pipeline
   *  + the `recordDeliveryReceipt` call; this surface only renders the
   *  affordance. Hidden when undefined. */
  onAttachDeliveryPhoto?: (item: BomItem) => void;
  /** Opens quote upload from BoM row when suppliers are shortlisted. */
  onAddQuote?: (bomItemId: string) => void;
  /** Email first shortlisted supplier for this BoM line. */
  onEmailSupplier?: (item: BomItem, supplierId: string) => void;
  /** Attach a supporting document to this BoM line. */
  onAttachBomDocument?: (item: BomItem) => void;
  /** Attach an awarded-contract document to this BoM line. */
  onAttachAwardDocument?: (item: BomItem) => void;
  /** Open the BoM-to-EO canvas review flow. */
  onGenerateSimulation?: () => void;
  /**
   * Upload-in-progress feedback. When an attachment upload is mid-flight
   * the parent sets this to the target BoM line + which attachment field
   * is being written. The matching row renders an "Uploading…" indicator
   * until the parent clears it (success or error). Undefined = idle. */
  attachBusy?: { itemId: string; field: BomAttachmentField } | null;
  /** Open the document viewer for a linked attachment (by document id). */
  onOpenDocument?: (documentId: string) => void;
  /** Unlink a previously attached document from this BoM line + field. */
  onRemoveBomDocument?: (item: BomItem, field: BomAttachmentField, documentId: string) => void;
}

/** Which attachment array on a BoM item a document is linked under. */
export type BomAttachmentField = 'attachments' | 'awardAttachments';

const CATEGORY_ORDER: SCMCategory[] = [
  'power-generation',
  'power-conversion',
  'energy-storage',
  'cooling',
  'data-center-it',
  'connectivity',
  'civil-construction',
  'logistics',
  'commissioning',
];

/** Stable, complete ordering for the BOM picker; deliberately never truncates. */
export function orderEquipmentCatalogChoices(
  entries: readonly EquipmentCatalogEntry[],
): EquipmentCatalogEntry[] {
  return [...entries].sort((a, b) => (
    a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
    || a.id.localeCompare(b.id)
  ));
}

function formatUsd(value: number | null): string {
  if (value == null || !Number.isFinite(value)) return '—';
  if (value >= 1_000_000_000) return `$${(value / 1_000_000_000).toFixed(2)}B`;
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(1)}k`;
  return `$${value.toFixed(0)}`;
}

function statusTone(status: BomItem['status']): string {
  switch (status) {
    case 'planned':
      return 'bg-muted text-text-secondary';
    case 'rfq-out':
    case 'quoted':
      return 'bg-primary/15 text-primary';
    case 'ordered':
    case 'in-transit':
      return 'bg-warning/15 text-warning';
    case 'on-site':
    case 'commissioned':
      return 'bg-success/15 text-success';
  }
}

/** Price-provenance dot tone for the line-CAPEX cell (datasheet provenance). */
function priceSourceTone(src: BomItem['priceSource']): { cls: string; title: string } {
  switch (src) {
    case 'quote':
      return { cls: 'eq-src__dot--quote', title: 'Price from quote' };
    case 'catalog':
      return { cls: 'eq-src__dot--catalog', title: 'Price from equipment catalog' };
    case 'manual':
      return { cls: 'eq-src__dot--catalog', title: 'Manually entered price' };
    default:
      return { cls: 'eq-src__dot--baseline', title: 'Industry baseline' };
  }
}

/** Lead-time band tone — long bands flag shortage risk on the schedule. */
function leadToneClass(item: BomItem): string {
  if (item.leadTimeSource === 'unknown') return 'da-cell-num--warn';
  if (item.leadTimeMonthsHigh >= 36) return 'da-cell-num--danger';
  if (item.leadTimeMonthsHigh >= 24) return 'da-cell-num--warn';
  return '';
}

function criticalityGlyph(item: BomItem): React.ReactNode {
  if (item.criticality === 'critical-path') {
    return <Star className="w-3 h-3 text-warning" aria-label="critical-path" />;
  }
  // Shortage hint — long high band on a power-conversion item.
  if (item.category === 'power-conversion' && item.leadTimeMonthsHigh >= 24) {
    return <AlertTriangle className="w-3 h-3 text-warning" aria-label="shortage risk" />;
  }
  return <span className="w-3 h-3 inline-block rounded-full bg-text-secondary/40" aria-hidden="true" />;
}

/** Cert display label — taxonomy name when known, id verbatim otherwise. */
function certLabel(id: ComplianceCertificationId): string {
  return getComplianceCertificationById(id)?.name ?? id;
}

export const SupplyChainBomPanel: React.FC<SupplyChainBomPanelProps> = ({
  plan,
  suppliers = plan?.suppliers ?? [],
  companyId,
  onImportBom,
  onSyncSiteWorksFromLayout,
  onJumpToSuppliersWithFilter,
  onCreateItem,
  onCreateSupplier,
  onUpdateItem,
  onDeleteItem,
  onStatusTransition,
  onAttachDeliveryPhoto,
  onAddQuote,
  onEmailSupplier,
  onAttachBomDocument,
  onAttachAwardDocument,
  onGenerateSimulation,
  attachBusy,
  onOpenDocument,
  onRemoveBomDocument,
}) => {
  const [categoryFilter, setCategoryFilter] = useState<'all' | SCMCategory>('all');
  const [criticalOnly, setCriticalOnly] = useState(false);
  const [search, setSearch] = useState('');
  const [collapsed, setCollapsed] = useState<Set<SCMCategory>>(new Set());
  const [editorMode, setEditorMode] = useState<'add' | 'edit' | null>(null);
  const [editingItem, setEditingItem] = useState<BomItem | undefined>(undefined);
  const [catalogEntries, setCatalogEntries] = useState<EquipmentCatalogEntry[]>([]);

  useEffect(() => {
    void resolveEquipmentCatalog(companyId)
      .then(setCatalogEntries)
      .catch(() => setCatalogEntries([]));
  }, [companyId]);

  const catalogChoices = useMemo(
    () => orderEquipmentCatalogChoices(catalogEntries),
    [catalogEntries],
  );

  const filteredByCategory = useMemo(() => {
    const bom = plan?.bom ?? [];
    const q = search.trim().toLowerCase();
    const groups: Record<string, BomItem[]> = {};
    for (const item of bom) {
      if (categoryFilter !== 'all' && item.category !== categoryFilter) continue;
      if (criticalOnly && item.criticality !== 'critical-path') continue;
      if (q && !(`${item.name} ${item.spec}`.toLowerCase().includes(q))) continue;
      (groups[item.category] = groups[item.category] ?? []).push(item);
    }
    return groups;
  }, [plan?.bom, categoryFilter, criticalOnly, search]);

  const totalItems = Object.values(filteredByCategory).reduce((n, g) => n + g.length, 0);
  const totalCost = Object.values(filteredByCategory)
    .flat()
    .reduce((sum, i) => sum + (i.totalCostUsd ?? 0), 0);

  if (!plan || plan.bom.length === 0) {
    return (
      <div data-testid="scm-bom-empty">
        <EmptyState
          icon={Package}
          title="No bill of materials yet"
          body="No BoM seeded yet. Open the CAPEX tab to generate one from your Project Intent + Financial Model, or upload an existing schedule from your EPC partner."
        />
      </div>
    );
  }

  const toggleCategory = (cat: SCMCategory) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });

  const closeEditor = () => {
    setEditorMode(null);
    setEditingItem(undefined);
  };

  const openAddDialog = () => {
    setEditingItem(undefined);
    setEditorMode('add');
  };

  const openEditDialog = (item: BomItem) => {
    setEditingItem(item);
    setEditorMode('edit');
  };

  const handleSubmitDialog = async (payload: Omit<BomItem, 'id' | 'derivedFrom'>) => {
    if (editorMode === 'add') await onCreateItem?.(payload);
    if (editorMode === 'edit' && editingItem) await onUpdateItem?.(editingItem.id, payload);
    closeEditor();
  };

  return (
    <div className="space-y-4 scm-bom" data-testid="scm-bom-panel">
      {/* Schedule-of-equipment datasheet panel */}
      <section className="da-panel">
        <div className="da-panel__head">
          <span className="da-type-eyebrow">
            Schedule of equipment · {totalItems} lines · {formatUsd(totalCost)}
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onImportBom}
              disabled={!onImportBom}
              title={onImportBom ? 'Import BoM lines from a PDF or Excel schedule.' : 'Import unavailable.'}
              className="da-btn"
              data-testid="scm-bom-import"
            >
              Import BoM
            </button>
            <button
              type="button"
              onClick={() => { void onSyncSiteWorksFromLayout?.(); }}
              disabled={!onSyncSiteWorksFromLayout}
              title={onSyncSiteWorksFromLayout
                ? 'Merge screening site-works quantities (fencing, pads, roads, clearing) from the promoted generation layout. Preserves pricing and status on existing lines.'
                : 'Promote a generation scenario in Generation & Impact first.'}
              className="da-btn"
              data-testid="scm-bom-sync-siteworks"
            >
              Sync site-works from layout
            </button>
            {onGenerateSimulation && isBomSimulationGenerationEnabled() && (
              <Button
                onClick={onGenerateSimulation}
                disabled={!plan?.bom?.length}
                title={plan?.bom?.length ? 'Generate an editable canvas from this BoM, then complete Simulation readiness.' : 'Add BoM items first'}
                variant="outline"
                size="sm"
                data-testid="scm-bom-generate-simulation"
              >
                <Cog className="w-3.5 h-3.5 mr-1" />
                Generate Simulation
              </Button>
            )}
            {onCreateItem && (
              <select
                className="scm-bom__select"
                defaultValue=""
                onChange={(e) => {
                  const id = e.target.value;
                  if (!id) return;
                  const entry = catalogEntries.find(c => c.id === id);
                  if (entry) void onCreateItem(catalogEntryToBomDraft(entry));
                  e.target.value = '';
                }}
                data-testid="scm-bom-add-from-catalog"
              >
                <option value="">Add from catalog…</option>
                {catalogChoices.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            )}
            <button
              type="button"
              onClick={openAddDialog}
              disabled={!onCreateItem}
              title={onCreateItem ? 'Add a new BoM line item.' : 'Add item unavailable for this context.'}
              className="da-btn da-btn--primary"
              data-testid="scm-bom-add-item"
            >
              + Add item
            </button>
          </div>
        </div>

        {/* Filters row */}
        <div className="scm-bom__filters" data-testid="scm-bom-filters">
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value as 'all' | SCMCategory)}
            className="scm-bom__select"
            data-testid="scm-bom-category-filter"
          >
            <option value="all">All categories</option>
            {CATEGORY_ORDER.map((cat) => (
              <option key={cat} value={cat}>{SCM_CATEGORY_LABELS[cat]}</option>
            ))}
          </select>
          <label className="scm-bom__check">
            <input
              type="checkbox"
              checked={criticalOnly}
              onChange={(e) => setCriticalOnly(e.target.checked)}
              data-testid="scm-bom-critical-toggle"
            />
            Critical-path only
          </label>
          <div className="scm-bom__search">
            <Search className="scm-bom__search-ic" aria-hidden />
            <input
              type="text"
              placeholder="Search items…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="scm-bom-search"
            />
          </div>
        </div>

        {totalItems === 0 ? (
          <div className="da-panel__body">
            <div
              className="text-center py-6 border border-dashed border-border type-body text-text-secondary"
              data-testid="scm-bom-no-matches"
            >
              No items match the current filters.
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {CATEGORY_ORDER.filter((cat) => (filteredByCategory[cat]?.length ?? 0) > 0).map((cat) => {
              const items = filteredByCategory[cat]!;
              const groupCost = items.reduce((s, i) => s + (i.totalCostUsd ?? 0), 0);
              const isCollapsed = collapsed.has(cat);
              return (
                <div
                  key={cat}
                  className="scm-bom__group"
                  data-testid={`scm-bom-group-${cat}`}
                >
                  <button
                    type="button"
                    onClick={() => toggleCategory(cat)}
                    className="scm-bom__grouphead"
                    aria-expanded={!isCollapsed}
                  >
                    {isCollapsed ? (
                      <ChevronRight className="w-3 h-3 text-primary shrink-0" aria-hidden />
                    ) : (
                      <ChevronDown className="w-3 h-3 text-primary shrink-0" aria-hidden />
                    )}
                    <span className="scm-bom__groupname">{SCM_CATEGORY_LABELS[cat]}</span>
                    <span className="scm-bom__groupmeta">
                      {items.length} {items.length === 1 ? 'line' : 'lines'} · {formatUsd(groupCost)}
                    </span>
                    <span className="scm-bom__groupbar" aria-hidden />
                  </button>
                  {!isCollapsed && (
                    <div className="scm-bom__tablewrap">
                      <table className="da-table scm-bom__table">
                        <thead>
                          <tr>
                            <th className="scm-bom__crit" aria-label="Criticality" />
                            <th className="scm-bom__th-left">Line item</th>
                            <th>Qty</th>
                            <th>Lead (mo)</th>
                            <th>Status</th>
                            <th>Line CAPEX</th>
                          </tr>
                        </thead>
                        <tbody>
                          {items.map((item) => {
                            const provenance = priceSourceTone(item.priceSource);
                            return (
                              <React.Fragment key={item.id}>
                                <tr
                                  className="scm-bom__row"
                                  data-testid={`scm-bom-item-${item.id}`}
                                >
                                  <td className="scm-bom__crit">{criticalityGlyph(item)}</td>
                                  <td className="da-cell-name scm-bom__th-left">
                                    <span className="scm-bom__namewrap">
                                      <span className="scm-bom__name">{item.name}</span>
                                      {hasDeliveryReceipt(item) && (
                                        <span
                                          className="scm-bom__minibadge scm-bom__minibadge--ok"
                                          title={`Delivery photo on file (${item.deliveryReceipt.fileName})`}
                                          data-testid={`scm-bom-item-${item.id}-delivery-badge`}
                                        >
                                          <Camera className="w-3 h-3" /> Receipt
                                        </span>
                                      )}
                                      {hasQuoteAvailableBadge(item) && (
                                        <span
                                          className="scm-bom__minibadge scm-bom__minibadge--warn"
                                          data-testid={`scm-bom-item-${item.id}-quote-available-badge`}
                                          title="A vendor quote with materially better cost or lead time is open — review and award on the Quotes tab."
                                        >
                                          <FileCheck className="w-3 h-3" /> Quote available — review
                                        </span>
                                      )}
                                    </span>
                                    {item.spec && <span className="da-cell-sub scm-bom__spec">{item.spec}</span>}
                                    {item.oemGenerationBasis && (
                                      <span className="da-cell-sub scm-bom__meta text-primary">
                                        OEM case · {item.oemGenerationBasis.manufacturer} {item.oemGenerationBasis.model}
                                        {' · '}{item.oemGenerationBasis.performanceCaseId}
                                        {' · '}{item.oemGenerationBasis.authority.replaceAll('-', ' ')}
                                      </span>
                                    )}
                                    {item.oemElectricalMachineBasis && (
                                      <span className="da-cell-sub scm-bom__meta text-primary">
                                        OEM alternator · {item.oemElectricalMachineBasis.manufacturer} {item.oemElectricalMachineBasis.model}
                                        {' · '}{item.oemElectricalMachineBasis.ratedKva} kVA @ {item.oemElectricalMachineBasis.ratedVoltageKv} kV
                                      </span>
                                    )}
                                    <span className="da-cell-sub scm-bom__meta">
                                      {item.unit} @ {formatUsd(item.unitCostUsd)} · {labelBomSubstitutability(item.substitutability)}
                                      {item.phaseId ? ` · ${item.phaseId}` : ''}
                                    </span>
                                  </td>
                                  <td>{item.quantity}</td>
                                  <td className={leadToneClass(item)}>
                                    {item.leadTimeSource === 'unknown'
                                      ? 'Quote required'
                                      : `${item.leadTimeMonthsLow}–${item.leadTimeMonthsHigh}`}
                                  </td>
                                  <td>
                                    <BomStatusControl
                                      item={item}
                                      onTransition={onStatusTransition}
                                      toneClass={statusTone(item.status)}
                                    />
                                  </td>
                                  <td>
                                    <span className="eq-src">
                                      <span className={`eq-src__dot ${provenance.cls}`} title={provenance.title} />
                                      <span className="eq-src__tot">{formatUsd(item.totalCostUsd)}</span>
                                    </span>
                                  </td>
                                </tr>
                                {/* Detail row — linked docs, compliance match, row actions. */}
                                <tr className="scm-bom__detail">
                                  <td />
                                  <td colSpan={5} className="scm-bom__th-left">
                                    {notesWithoutBadge(item).trim() && (
                                      <details className="mb-2 text-xs text-text-secondary" data-testid={`scm-bom-notes-${item.id}`}>
                                        <summary className="cursor-pointer">Notes and scope</summary>
                                        <p className="mt-1 whitespace-pre-wrap break-words">{notesWithoutBadge(item)}</p>
                                      </details>
                                    )}
                                    <BomLinkedDocs
                                      item={item}
                                      attachBusy={attachBusy}
                                      onOpenDocument={onOpenDocument}
                                      onRemoveBomDocument={onRemoveBomDocument}
                                    />
                                    <ComplianceMatchStrip
                                      item={item}
                                      plan={plan}
                                      suppliers={suppliers}
                                      catalog={catalogEntries}
                                      onJumpToSuppliersWithFilter={onJumpToSuppliersWithFilter}
                                    />
                                    <BomItemRowActions
                                      item={item}
                                      plan={plan}
                                      onAddQuote={onAddQuote}
                                      onEmailSupplier={onEmailSupplier}
                                      onAttachBomDocument={onAttachBomDocument}
                                      onAttachAwardDocument={onAttachAwardDocument}
                                      onAttachDeliveryPhoto={onAttachDeliveryPhoto}
                                      onEdit={onUpdateItem ? () => openEditDialog(item) : undefined}
                                      onDelete={onDeleteItem ? () => { void onDeleteItem(item); } : undefined}
                                    />
                                  </td>
                                </tr>
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      <style>{BOM_CSS}</style>

      <BomItemEditDialog
        open={editorMode !== null}
        mode={editorMode === 'edit' ? 'edit' : 'add'}
        initialItem={editingItem}
        suppliers={suppliers}
        companyId={companyId}
        onCreateSupplier={onCreateSupplier}
        onCancel={closeEditor}
        onSubmit={(payload) => { void handleSubmitDialog(payload); }}
      />
    </div>
  );
};

/**
 * Surface-local Aurora Cockpit chrome for the BOM datasheet (round-2
 * `round2-equipment.html`): category group-bands, the recessed filter row,
 * the criticality glyph column, the price-provenance dot on the CAPEX cell,
 * and the detail row that carries linked-docs / compliance / actions. Layers
 * ON TOP of the shared `.da-*` tokens — no token redefinition.
 */
const BOM_CSS = `
.scm-bom__filters { display: flex; align-items: center; gap: .5rem; flex-wrap: wrap; padding: .75rem var(--panel-pad); border-bottom: 1px solid var(--border-subtle); }
.scm-bom__select { font-family: var(--font-mono); font-size: .6875rem; color: var(--text-secondary); background: var(--inset); border: 1px solid var(--border); border-radius: .375rem; padding: .4375rem .625rem; max-width: 13rem; }
.scm-bom__select:focus { outline: none; border-color: color-mix(in srgb, var(--primary) 55%, var(--border)); }
.scm-bom__check { display: inline-flex; align-items: center; gap: .4375rem; font-family: var(--font-mono); font-size: .625rem; letter-spacing: .04em; text-transform: uppercase; color: var(--text-muted); cursor: pointer; }
.scm-bom__check input { accent-color: var(--primary); }
.scm-bom__search { position: relative; flex: 1; min-width: 11rem; max-width: 22rem; }
.scm-bom__search input { width: 100%; font-family: var(--font-mono); font-size: .6875rem; color: var(--text-secondary); background: var(--inset); border: 1px solid var(--border); border-radius: .375rem; padding: .4375rem .625rem .4375rem 2rem; }
.scm-bom__search input:focus { outline: none; border-color: color-mix(in srgb, var(--primary) 55%, var(--border)); }
.scm-bom__search-ic { position: absolute; left: .625rem; top: 50%; transform: translateY(-50%); width: .875rem; height: .875rem; color: var(--text-muted); }
/* group band */
.scm-bom__grouphead { display: flex; align-items: baseline; gap: .75rem; width: 100%; text-align: left; background: var(--inset); border: 0; border-top: 1px solid var(--border); border-bottom: 1px solid var(--border); padding: .625rem var(--panel-pad); cursor: pointer; }
.scm-bom__grouphead:hover { background: var(--state-hover); }
.scm-bom__groupname { font-family: var(--font-mono); font-size: .6875rem; font-weight: 600; letter-spacing: .14em; text-transform: uppercase; color: var(--primary); }
.scm-bom__groupmeta { font-family: var(--font-mono); font-size: .625rem; letter-spacing: .04em; color: var(--text-muted); }
.scm-bom__groupbar { flex: 1; height: 1px; align-self: center; background: linear-gradient(to right, var(--border), transparent); }
.scm-bom__tablewrap { overflow-x: auto; }
.scm-bom__table { min-width: 640px; }
.scm-bom__table th.scm-bom__th-left, .scm-bom__table td.scm-bom__th-left { text-align: left; }
.scm-bom__crit { width: 1.5rem; text-align: center !important; padding-left: var(--panel-pad); padding-right: 0 !important; }
.scm-bom__row td { vertical-align: top; border-bottom: 0; }
.scm-bom__detail td { padding-top: 0; padding-bottom: .75rem; border-bottom: 1px solid var(--border-subtle); }
.scm-bom__detail td:empty { padding: 0; }
.scm-bom__namewrap { display: inline-flex; align-items: center; gap: .5rem; flex-wrap: wrap; }
.scm-bom__name { color: var(--text-primary); }
.scm-bom__spec { max-width: 28rem; white-space: normal; line-height: 1.45; }
.scm-bom__minibadge { display: inline-flex; align-items: center; gap: .25rem; font-family: var(--font-mono); font-size: .5rem; font-weight: 600; letter-spacing: .06em; text-transform: uppercase; padding: .0625rem .375rem; border-radius: .25rem; border: 1px solid var(--border); }
.scm-bom__minibadge--ok { color: var(--success); border-color: color-mix(in srgb, var(--success) 40%, transparent); background: color-mix(in srgb, var(--success) 10%, transparent); }
.scm-bom__minibadge--warn { color: var(--warning); border-color: color-mix(in srgb, var(--warning) 40%, transparent); background: color-mix(in srgb, var(--warning) 10%, transparent); }
.eq-src { display: inline-flex; align-items: center; gap: .3125rem; justify-content: flex-end; }
.eq-src__dot { width: .375rem; height: .375rem; border-radius: 50%; flex-shrink: 0; }
.eq-src__dot--quote { background: var(--success); box-shadow: 0 0 6px color-mix(in srgb, var(--success) 70%, transparent); }
.eq-src__dot--catalog { background: var(--primary); }
.eq-src__dot--baseline { background: var(--text-disabled); }
.eq-src__tot { color: var(--text-primary); font-weight: 600; }
`;

// ─────────────────────────────────────────────────────────────────────────────
// Per-item linked-documents list.
//
// Replaces the old count-only string ("N BoM docs · M award docs") with an
// actual list of the documents attached to this BoM line so the user can SEE
// what is linked — open each in the viewer and unlink it. While an attachment
// upload for this row is mid-flight (`attachBusy` matches this item + field)
// an "Uploading…" indicator renders against the relevant group.
// ─────────────────────────────────────────────────────────────────────────────

interface BomLinkedDocsProps {
  item: BomItem;
  attachBusy?: { itemId: string; field: BomAttachmentField } | null;
  onOpenDocument?: (documentId: string) => void;
  onRemoveBomDocument?: (item: BomItem, field: BomAttachmentField, documentId: string) => void;
}

const ATTACHMENT_GROUPS: { field: BomAttachmentField; label: string }[] = [
  { field: 'attachments', label: 'BoM documents' },
  { field: 'awardAttachments', label: 'Award documents' },
];

const BomLinkedDocs: React.FC<BomLinkedDocsProps> = ({
  item,
  attachBusy,
  onOpenDocument,
  onRemoveBomDocument,
}) => {
  const hasAny =
    (item.attachments?.length ?? 0) > 0 || (item.awardAttachments?.length ?? 0) > 0;
  const busyHere = attachBusy?.itemId === item.id;
  // Nothing to show and nothing uploading → render nothing (matches prior behaviour).
  if (!hasAny && !busyHere) return null;

  return (
    <div className="space-y-1.5" data-testid={`scm-bom-docs-${item.id}`}>
      {ATTACHMENT_GROUPS.map(({ field, label }) => {
        const docs = (item[field] as SupplyChainAttachmentRef[] | undefined) ?? [];
        const uploadingThisField = busyHere && attachBusy?.field === field;
        if (docs.length === 0 && !uploadingThisField) return null;
        return (
          <div key={field} className="space-y-1" data-testid={`scm-bom-docs-${item.id}-${field}`}>
            <div className="type-meta uppercase tracking-widest text-text-muted">
              {label} ({docs.length})
            </div>
            <ul className="space-y-1">
              {docs.map((doc) => (
                <li
                  key={doc.documentId}
                  className="flex items-center gap-1.5 type-body"
                  data-testid={`scm-bom-doc-${item.id}-${doc.documentId}`}
                >
                  <FileText className="w-3 h-3 shrink-0 text-text-secondary" aria-hidden="true" />
                  <button
                    type="button"
                    onClick={() => onOpenDocument?.(doc.documentId)}
                    disabled={!onOpenDocument}
                    title={onOpenDocument ? 'Open document' : 'Document viewer unavailable'}
                    className={cn(
                      'min-w-0 flex-1 truncate text-left',
                      onOpenDocument
                        ? 'text-primary underline-offset-2 hover:underline'
                        : 'text-text-secondary cursor-default',
                    )}
                    data-testid={`scm-bom-doc-open-${item.id}-${doc.documentId}`}
                  >
                    {doc.fileName ?? doc.documentId}
                  </button>
                  {onRemoveBomDocument && (
                    <button
                      type="button"
                      onClick={() => onRemoveBomDocument(item, field, doc.documentId)}
                      title="Unlink document from this item"
                      aria-label={`Unlink ${doc.fileName ?? 'document'}`}
                      className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-sm text-text-muted transition-colors hover:bg-danger/10 hover:text-danger"
                      data-testid={`scm-bom-doc-remove-${item.id}-${doc.documentId}`}
                    >
                      <X className="w-3 h-3" aria-hidden="true" />
                    </button>
                  )}
                </li>
              ))}
              {uploadingThisField && (
                <li
                  className="flex items-center gap-1.5 type-body text-text-secondary"
                  data-testid={`scm-bom-doc-uploading-${item.id}-${field}`}
                >
                  <Loader2 className="w-3 h-3 shrink-0 animate-spin" aria-hidden="true" />
                  Uploading…
                </li>
              )}
            </ul>
          </div>
        );
      })}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// SCM13 — per-row compliance chip strip + "N suppliers match" affordance.
//
// Memoises the matcher result per row. Clicking the count navigates to the
// Suppliers tab via the `onJumpToSuppliersWithFilter` callback (when wired).
// ─────────────────────────────────────────────────────────────────────────────

interface ComplianceMatchStripProps {
  item: BomItem;
  plan: SupplyChainPlan | undefined;
  suppliers?: readonly Supplier[];
  catalog?: readonly EquipmentCatalogEntry[];
  onJumpToSuppliersWithFilter?: (filter: readonly ComplianceCertificationId[], bomItemId?: string) => void;
}

const ComplianceMatchStrip: React.FC<ComplianceMatchStripProps> = ({
  item,
  plan,
  suppliers,
  catalog,
  onJumpToSuppliersWithFilter,
}) => {
  // Normalise once per render so the chip key and the matcher agree.
  const requirementIds = useMemo(
    () => normalizeRequirements(item.complianceRequirements),
    [item.complianceRequirements],
  );
  const matchCount = useMemo(
    () => suppliersMatchingBomItem(plan, item, suppliers, catalog).length,
    [plan, item, suppliers, catalog],
  );

  // Render BoM-rule chips using taxonomy display names so the row reads
  // consistently with the Suppliers tab strip even when the source data
  // carried an alias (e.g. 'IEEE C57.12.00' → 'ANSI/IEEE C57.12.00').
  return (
    <div className="flex flex-wrap items-center gap-1 mt-1" data-testid={`scm-bom-compliance-${item.id}`}>
      {requirementIds.map((id) => (
        <span
          key={id}
          className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-muted border border-border type-meta uppercase tracking-widest text-text-secondary"
        >
          <ShieldCheck className="w-2.5 h-2.5" />
          {certLabel(id)}
        </span>
      ))}
      <button
        type="button"
        aria-label={`Find suppliers for ${item.name}`}
        onClick={() => onJumpToSuppliersWithFilter?.(requirementIds, item.id)}
        disabled={!onJumpToSuppliersWithFilter}
        title={
          matchCount === 0
            ? 'No recorded equipment links satisfy this item’s requirements. Open suppliers to review the selected equipment or browse its category.'
            : `${matchCount} recorded equipment matches satisfy this item’s certification requirements. Confirm current model, availability and terms with the supplier.`
        }
        className={cn(
          'inline-flex items-center gap-1 px-1.5 py-0.5 border type-meta uppercase tracking-widest transition-colors',
          matchCount === 0
            ? 'border-warning/40 text-warning bg-warning/10'
            : 'border-primary/40 text-primary bg-primary/10 hover:bg-primary/20',
          !onJumpToSuppliersWithFilter && 'cursor-not-allowed opacity-60',
        )}
        data-testid={`scm-bom-match-count-${item.id}`}
      >
        <Users className="w-2.5 h-2.5" />
        {matchCount} equipment match{matchCount === 1 ? '' : 'es'}
      </button>
    </div>
  );
};
